import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Button,
  Card,
  ErrorMessage,
  Flex,
  FullscreenTakeover,
  LabeledData,
  Link,
  LoadingIndicator,
  Select,
  Text,
  Textarea,
} from "@jsq/ds/core";
import { Column } from "@jsq/ds/layouts";
import { Sizes } from "@jsq/ds/styles";
import { globalEnv } from "~/global-env";
import { ComplianceCaseTypeEnum as CaseType } from "~/graphql/supergraphEnums";
import { useGetPicrPositionDetailsQuery } from "~/graphql/supergraphHooks";
import { type GetComplianceCaseQuery } from "~/graphql/supergraphTypes";
import { ComplianceRulesContext } from "../../../complianceRulesContext";
import { isEscalationRevampCaseType } from "../../../utils/constants";
import { showsEscalationPreview, showsProfileSelect } from "../escalationCaseTypes";
import { getProfileNameForEscalation } from "../ProposedEscalationsModal/utils";
import { createButtonLabel } from "./escalationPreview";
import { EscalationPreviewCard, type EscalationPreviewState } from "./EscalationPreviewCard";
import { type EscalationRecord, useEscalationForm } from "./useEscalationForm";

type Mode = "create" | "edit" | "view";

type ComplianceCase = NonNullable<GetComplianceCaseQuery["complianceCase"]>;

type Props = {
  mode: Mode;
  caseId: number;
  complianceCase: GetComplianceCaseQuery["complianceCase"] | null | undefined;
};

const TITLES: Record<Mode, string> = {
  create: "Create escalation",
  edit: "Edit escalation",
  view: "Proposed escalation",
};

type FormPanelProps = {
  mode: "create" | "edit";
  caseId: number;
  complianceCase: ComplianceCase;
  escalation: EscalationRecord | undefined;
  requestedProfileId: number | undefined;
  onCancel: () => void;
  onDone: () => void;
};

const EscalationFormPanel = ({
  mode,
  caseId,
  complianceCase,
  escalation,
  requestedProfileId,
  onCancel,
  onDone,
}: FormPanelProps) => {
  const caseType = complianceCase.caseType;
  const form = useEscalationForm({
    caseId,
    caseType,
    profileId: requestedProfileId,
    profilesForCase: complianceCase,
    mode,
    escalationToEdit: escalation,
    onDone,
  });
  const [preview, setPreview] = useState<EscalationPreviewState>({ count: 0, loading: false });

  return (
    <>
      <Card data-testid="escalation-form-card">
        <Flex flexDirection="column" gap={Sizes.MD}>
          <Text variant="h3">{TITLES[mode]}</Text>
          {showsProfileSelect(caseType) && (
            <Flex flexDirection="column" gap={Sizes.XS}>
              <Select
                label="Profile"
                placeholder="Select a profile"
                value={form.selectedProfileId}
                onChange={(value) => form.setSelectedProfileId(Number(value))}
                full
              >
                <Select.Section>
                  {form.profileOptions.map((opt) => (
                    <Select.Option value={opt.value} key={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
              {form.hasDuplicateEscalationForSelectedProfile && form.selectedProfileName && (
                <ErrorMessage>
                  {`There is already a proposed or outstanding escalation for ${form.selectedProfileName}. A profile can only have one proposed or outstanding escalation per originating case.`}
                </ErrorMessage>
              )}
            </Flex>
          )}
          <Textarea
            label="Issue"
            name="issue"
            placeholder="Describe the reason for escalating the selected profile"
            value={form.issueInput}
            isFullWidth
            errorMessage={form.errorVisible ? "Required" : undefined}
            onSetValue={form.setIssue}
          />
        </Flex>
      </Card>
      {showsEscalationPreview(caseType) && (
        <EscalationPreviewCard
          caseId={caseId}
          caseType={caseType}
          complianceCase={complianceCase}
          escalatedProfileId={form.effectiveProfileId}
          onPreviewChange={setPreview}
        />
      )}
      <Flex justifyContent="flex-end" gap={Sizes.SM}>
        {mode === "edit" && (
          <Button
            variant="danger"
            onClick={() => void form.removeEscalation()}
            isLoading={form.deleteLoading}
            data-testid="delete-escalation-button"
          >
            Delete escalation
          </Button>
        )}
        <Button variant="neutral" onClick={onCancel} data-testid="cancel-button">
          Cancel
        </Button>
        {mode === "edit" ? (
          <Button
            variant="primary"
            color="green"
            onClick={() => void form.saveEdit()}
            isLoading={form.updateLoading}
            disabled={form.hasDuplicateEscalationForSelectedProfile}
            data-testid="save-escalation-button"
          >
            Save
          </Button>
        ) : (
          <Button
            variant="primary"
            color="green"
            onClick={() => void form.submitCreate()}
            isLoading={form.createLoading || preview.loading}
            disabled={form.hasDuplicateEscalationForSelectedProfile}
            data-testid="create-button"
          >
            {createButtonLabel(preview.count)}
          </Button>
        )}
      </Flex>
    </>
  );
};

type ViewPanelProps = {
  caseId: number;
  complianceCase: ComplianceCase;
  escalation: EscalationRecord;
  onClose: () => void;
};

const EscalationViewPanel = ({
  caseId,
  complianceCase,
  escalation,
  onClose,
}: ViewPanelProps) => {
  const caseType = complianceCase.caseType;
  const isPicrCase = caseType === CaseType.PaymentInstructionChangeReview;
  const { data: picrPositionData } = useGetPicrPositionDetailsQuery({
    variables: { caseId },
    skip: !isPicrCase,
  });
  const profileId = escalation.profileId ?? complianceCase.investingProfile?.id;
  const profileName = getProfileNameForEscalation(
    complianceCase,
    escalation,
    isPicrCase,
    picrPositionData,
  );

  return (
    <>
      <Card data-testid="escalation-view-card">
        <Flex flexDirection="column" gap={Sizes.MD}>
          <Text variant="h3">{TITLES.view}</Text>
          <LabeledData
            variant="row"
            label="Profile"
            value={
              profileId == null ? (
                profileName
              ) : (
                <Link
                  to={`${globalEnv.complianceRoute}/case/${caseId}/profile-details?profileId=${profileId}`}
                >
                  {profileName}
                </Link>
              )
            }
          />
          <LabeledData
            variant="row"
            label="Escalation issue"
            value={escalation.description ?? ""}
          />
        </Flex>
      </Card>
      {showsEscalationPreview(caseType) && (
        <EscalationPreviewCard
          caseId={caseId}
          caseType={caseType}
          complianceCase={complianceCase}
          escalatedProfileId={profileId ?? undefined}
        />
      )}
      <Flex justifyContent="flex-end">
        <Button variant="neutral" onClick={onClose} data-testid="close-button">
          Close
        </Button>
      </Flex>
    </>
  );
};

const EscalationTakeover = ({ mode, caseId, complianceCase }: Props) => {
  const history = useHistory();
  const location = useLocation<{ from?: string } | undefined>();
  const { escalationId } = useParams<{ escalationId?: string }>();
  const rules = useContext(ComplianceRulesContext);

  const returnPath =
    location.state?.from ??
    `${globalEnv.complianceRoute}/case/${caseId}/${rules.defaultTabRoute}`;
  const exit = useCallback(() => history.push(returnPath), [history, returnPath]);
  const leave = useCallback(() => history.replace(returnPath), [history, returnPath]);

  const escalation = complianceCase?.escalations?.find((e) => e?.id === Number(escalationId));
  const requestedProfileId =
    Number(new URLSearchParams(location.search).get("profileId")) || undefined;

  // Only escalation-revamp case types use the takeover; anything else keeps the legacy modal.
  const notFound =
    !!complianceCase &&
    (!isEscalationRevampCaseType(complianceCase.caseType) ||
      (mode !== "create" && !escalation));
  useEffect(() => {
    if (notFound) leave();
  }, [notFound, leave]);

  const renderPanel = () => {
    if (!complianceCase || notFound) {
      return <LoadingIndicator />;
    }
    if (mode === "view" && escalation) {
      return (
        <EscalationViewPanel
          caseId={caseId}
          complianceCase={complianceCase}
          escalation={escalation}
          onClose={exit}
        />
      );
    }
    return (
      <EscalationFormPanel
        mode={mode === "edit" ? "edit" : "create"}
        caseId={caseId}
        complianceCase={complianceCase}
        escalation={escalation}
        requestedProfileId={requestedProfileId}
        onCancel={exit}
        onDone={leave}
      />
    );
  };

  return (
    <FullscreenTakeover data-testid="escalation-takeover">
      <FullscreenTakeover.Header title={TITLES[mode]} onExit={exit} />
      <FullscreenTakeover.Body>
        <Column size="md">
          <Flex flexDirection="column" gap={Sizes.LG}>
            {renderPanel()}
          </Flex>
        </Column>
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};

export default EscalationTakeover;
